// client/src/components/world/ModulePrimitive.jsx
// Итерация 8.1: тонкий рендер одного модуля структуры.
//
// Использует moduleGeometry.js (единый источник геометрии).
// Переиспользуется в InteriorVisualizer (игра) и Module3DViewer (Ангара).
//
// ⚠️ Не знает про палубы, радиусы, фильтры — это делает InteriorVisualizer.
//    Только рендер одного модуля по его base_form + base_stats + visual.

import React, { useMemo } from 'react';
import { getModuleGeometry, renderGeometryJSX } from '../../utils/moduleGeometry';

// ============================================================================
// КОМПОНЕНТ
// ============================================================================

export default function ModulePrimitive({ module, highlight = false }) {
  // Безопасное извлечение позиции (всегда массив [x,y,z])
  const position = useMemo(() => {
    const p = module?.position;
    if (
      p &&
      typeof p === 'object' &&
      typeof p.x === 'number' &&
      typeof p.y === 'number' &&
      typeof p.z === 'number'
    ) {
      return [p.x, p.y, p.z];
    }
    return [0, 0, 0];
  }, [module?.position?.x, module?.position?.y, module?.position?.z]);

  // Безопасное извлечение поворота (радианы)
  const rotation = useMemo(() => {
    const r = module?.rotation;
    if (
      r &&
      typeof r === 'object' &&
      typeof r.x === 'number' &&
      typeof r.y === 'number' &&
      typeof r.z === 'number'
    ) {
      // В данных rotation в градусах (из ModuleLayout — angle * 180 / π).
      // Three.js ждёт радианы → конвертируем.
      const deg2rad = Math.PI / 180;
      return [r.x * deg2rad, r.y * deg2rad, r.z * deg2rad];
    }
    return [0, 0, 0];
  }, [module?.rotation?.x, module?.rotation?.y, module?.rotation?.z]);

  // Геометрия (мемоизируем — не пересчитываем при каждом рендере)
  const geom = useMemo(() => getModuleGeometry(module), [module]);

  // Материал
  const materialProps = useMemo(() => {
    const base = geom.material;
    if (highlight) {
      // Подсветка текущего модуля (где стоит персонаж)
      return {
        color: '#00e5ff',
        emissive: '#00e5ff',
        emissiveIntensity: 0.8,
        metalness: 0.6,
        roughness: 0.3,
      };
    }
    return {
      color: base.color,
      emissive: base.emissive || '#000000',
      emissiveIntensity: base.emissiveIntensity || 0,
      metalness: base.metalness ?? 0.5,
      roughness: base.roughness ?? 0.5,
    };
  }, [geom.material, highlight]);

  // Безопасное имя для отладки
  const debugName = module?.name || module?.instance_id || 'module';

  return (
    <mesh
      position={position}
      rotation={rotation}
      castShadow
      receiveShadow
      name={debugName}
    >
      {renderGeometryJSX(geom)}
      <meshStandardMaterial {...materialProps} />
    </mesh>
  );
}

// ============================================================================
// ЭКСПОРТЫ ДЛЯ ТЕСТИРОВАНИЯ (опционально)
// ============================================================================

export { getModuleGeometry };
